import React, { ReactNode, useCallback } from 'react';
import clsx from 'clsx';
import { UIFeatherIcon } from '../feather-icons';
import { useIntl } from 'react-intl';
import { UISystemIcon } from '../icons';
import * as styles from './ui-drag-list-item.scss'

export enum UIDragListItemTypes {
  NONE = 'none',
  ADD = 'add',
  REMOVE = 'remove'
}

export interface UIDragListItemProps {
  children?: ReactNode;
  className?: string;
  id: string;
  dimmed?: boolean;
  draggable?: boolean;
  onClick?: (id: string) => void;
  onDrag?: (event: React.DragEvent) => void;
  onDragEnd?: (event: React.DragEvent, id: string) => void;
  onDragStart?: (event: React.DragEvent, id: string) => void;
  style?: React.CSSProperties;
  type?: UIDragListItemTypes;
}

export const UIDragListItem = ({
  children,
  className,
  dimmed = false,
  draggable = true,
  id,
  onClick,
  onDrag,
  onDragEnd,
  onDragStart,
  style,
  type = UIDragListItemTypes.NONE
}: UIDragListItemProps) => {
  const intl = useIntl();

  const removeLabel = intl.formatMessage({
    id: 'ui.drag_list_item.remove',
    defaultMessage: 'Remove'
  });

  const handleClick = useCallback(() => {
    onClick?.(id);
  }, [id, onClick]);

  const handleDragStart = useCallback(
    (event: React.DragEvent) => {
      const id = event.currentTarget.getAttribute('data-id') || '';
      event.dataTransfer.setData('text/plain', id);
      onDragStart?.(event, id);
    },
    [onDragStart]
  );

  const handleDragEnd = useCallback(
    (event: React.DragEvent) => {
      const id = event.currentTarget.getAttribute('data-id') || '';
      onDragEnd?.(event, id);
    },
    [onDragEnd]
  );

  return (
    <div className={styles.outerContainer}>
      <div
        className={clsx(className, styles.container, { [styles.draggable]: draggable, [styles.dimmed]: dimmed })}
        data-id={id}
        style={style}
        draggable={draggable}
        onDrag={onDrag}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className={styles.content}>{children}</div>
        {type === UIDragListItemTypes.ADD && (
          <div className={clsx(styles.buttonContainer, styles.add)}>
            <div className={clsx(styles.icon, styles.dragIcon)}>
              <UISystemIcon iconName="drag-vertical" />
            </div>
          </div>
        )}
        {type === UIDragListItemTypes.REMOVE && (
          <button
            className={clsx(styles.buttonContainer, styles.remove, { [styles.clickable]: draggable })}
            disabled={!draggable}
            onClick={handleClick}
            aria-label={removeLabel}
          >
            <div className={clsx(styles.icon, styles.featherIcon)}>
              <UIFeatherIcon iconName="minus" />
            </div>
          </button>
        )}
        {type === UIDragListItemTypes.NONE && <div /> /* dummy element for flex layout */}
      </div>
    </div>
  );
};
